import clamp from 'lodash/clamp'
import { useMemo } from 'react'

import { PromoCardConfiguration } from '../types'

/**
 * Normalises the position of promo-cards to prevent white-space.
 * - Re-adjusts the index so each grid-cell is considered an index. This is mainly
 *   to help with reasoning.
 * - If a card is placed out-of-bounds it's position is re-aligned to a calid one.
 * @param promoCards The raw list of all promo-cards.
 * @param pageSize The size of the page.
 * @returns The promo-cards object with positions normalised.
 */
export const useNormalisedPromoCards = (
  promoCards: { [key: number]: PromoCardConfiguration },
  pageSize: number,
) => {
  const [normalisedPromoCards, finalIndexShift] = useMemo(() => {
    // Sort the keys in ascrending order so we're correctly
    // applying the right index-shift.
    const sortedKeys = Object.keys(promoCards)
      .map((key) => parseInt(key, 10))
      .sort((fst, snd) => fst - snd)

    return sortedKeys.reduce(
      ([promoCardsAccum, indexShift], key) => {
        const promoCard = promoCards[key]
        const page = Math.max(0, Math.floor(key / pageSize))

        let positionModifier = 0
        let indexShiftModifier = 0
        switch (promoCard.layout) {
          // Horizontal cards can't be placed on last cell
          case '2x1':
            positionModifier -= 1
            indexShiftModifier -= 1
            break
        }

        // Clamp the position of the card on the page.
        // e.g. A 2x1 horizontal card starting index can not be the last cell.
        const lowerBound = page * pageSize
        const upperBound =
          lowerBound + (pageSize - 1) + positionModifier + indexShift

        const normalisedKey = clamp(key + indexShift, lowerBound, upperBound)

        return [
          { ...promoCardsAccum, [normalisedKey]: promoCard },
          indexShift + indexShiftModifier,
        ]
      },
      [{} as { [key: number]: PromoCardConfiguration }, 0],
    )
  }, [promoCards])

  return { normalisedPromoCards, finalIndexShift }
}
